export interface ShopifyReadSource {
  provider: 'shopify';
  storeDomain: string;
  storeName?: string;
  readAt?: string;
  status: 'complete' | 'partial' | 'unavailable';
  coverage?: string;
}

export interface ShopifyHealth {
  state: 'connected' | 'reconnect_required' | 'store_paused' | 'store_unavailable' | 'rate_limited' | 'unavailable' | 'unknown';
  storeDomain: string;
  storeName?: string;
  lastSuccessfulReadAt?: string;
}

const STORE_DOMAIN = /^[a-z0-9][a-z0-9-]{0,59}\.myshopify\.com$/;

export function readShopifyReadSource(value: unknown): ShopifyReadSource | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const row = value as Record<string, unknown>;
  if (row.provider !== 'shopify' || typeof row.storeDomain !== 'string' || !STORE_DOMAIN.test(row.storeDomain)) return undefined;
  if (row.status !== 'complete' && row.status !== 'partial' && row.status !== 'unavailable') return undefined;
  return {
    provider: 'shopify',
    storeDomain: row.storeDomain,
    status: row.status,
    ...(typeof row.storeName === 'string' && row.storeName.trim() ? { storeName: row.storeName } : {}),
    ...(row.status !== 'unavailable' && typeof row.readAt === 'string' && Number.isFinite(Date.parse(row.readAt)) ? { readAt: row.readAt } : {}),
    ...(typeof row.coverage === 'string' && row.coverage.trim() ? { coverage: row.coverage } : {}),
  };
}

export function shopifyStoreAdminUrl(storeDomain: string): string | undefined {
  return STORE_DOMAIN.test(storeDomain) ? `https://${storeDomain}/admin` : undefined;
}

export function shopifyAdminUrl(health: ShopifyHealth): string | undefined {
  return shopifyStoreAdminUrl(health.storeDomain);
}

export function shopifyHealthLabelKey(health: ShopifyHealth): string {
  switch (health.state) {
    case 'unknown':
      return 'connectors.shopify.healthUnknown';
    case 'reconnect_required':
      return 'connectors.reconnectNeeded';
    case 'store_paused':
      return 'connectors.shopify.paused';
    case 'store_unavailable':
      return 'connectors.shopify.storeUnavailable';
    case 'rate_limited':
      return 'connectors.shopify.rateLimited';
    case 'unavailable':
      return 'connectors.shopify.readUnavailable';
    default:
      return 'common.connected';
  }
}

export function shopifyHealthReasonKey(health: ShopifyHealth): string | undefined {
  switch (health.state) {
    case 'unknown':
      return 'connectors.shopify.healthUnknown';
    case 'reconnect_required':
      return 'connectors.shopify.reconnectReason';
    case 'store_paused':
      return 'connectors.shopify.pausedReason';
    case 'store_unavailable':
      return 'connectors.shopify.storeUnavailableReason';
    case 'rate_limited':
      return 'connectors.shopify.rateLimited';
    case 'unavailable':
      return 'connectors.shopify.readUnavailable';
    default:
      return undefined;
  }
}
